/**
 * One person in a send (plan 2026-09-27-003, U6): the table row, and — once
 * opened — the detail row under it with "What was sent", "Who & where",
 * "What Clay did" and (when retried) "Retry history".
 *
 * The row itself is the toggle: a Glaze `TableRow` (`<tr>`) made keyboard
 * accessible (`tabIndex=0`, Enter/Space, `aria-expanded`). Buttons inside it
 * (Retry, Check again) stop propagation so they never also toggle the row.
 *
 * The sent-copy read (`GET …/dispatch-log/:logId/sent-copy`) runs ONLY while
 * the row is open (`enabled: open`), once per open row.
 */
import { useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { Button } from '@clay-run/glaze/button';
import { Chip } from '@clay-run/glaze/chip';
import { Icon } from '@clay-run/glaze/icon';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { TableCell, TableRow } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';

import { useApi } from '../../../../_shared/hooks';
import { fetchSentCopyRequest } from '../../../api-client';
import { isRetryEligibleRow } from '../../../dispatch-inspector-logic';
import { REP_STATUS_BADGE, type RepStatusView } from '../../../dispatch-rep-status';
import { playsPrefixKey } from '../../../query-keys';
import type { PlaybooksDispatchLogRow } from '../../../schemas';
import { ConfirmActionDialog } from '../../ConfirmActionDialog';

import { RetryHistoryPanel } from './RetryHistoryPanel';
import { SentCopyPanel } from './SentCopyPanel';
import { WhatClayDidPanel } from './WhatClayDidPanel';
import { WhoWherePanel } from './WhoWherePanel';
import type { SendRecheck } from './useSendRecheck';
import type { SendRetry } from './useSendRetry';

/**
 * Includes the row's OWN `retry_count` (U9, 2026-09-30) so a retry's refetch
 * of the `sends` query — already invalidated by `useSendRetry.ts`, which is
 * how `retry_count` itself changes — busts this key too and the open pane
 * refetches. Without it, a rep who fixed the copy and clicked Retry kept
 * seeing the REFUSED attempt's copy in "What was sent" even after the status
 * pill updated: the key never changed, so react-query never asked again.
 * `sent-copy.ts` reads `clay_final_copy` first (cleared by the refusal CAS)
 * then `payload` (overwritten with the rebuilt job), so the refetch alone is
 * enough — no separate invalidation needed here or in `useSendRetry.ts`.
 */
export function sentCopyQueryKey(
  playId: string,
  logId: string,
  retryCount: number,
): readonly [...ReturnType<typeof playsPrefixKey>, string, 'dispatch-log', string, 'sent-copy', string] {
  return [...playsPrefixKey(), playId, 'dispatch-log', logId, 'sent-copy', String(retryCount)];
}

/**
 * Extras the Dispatch log's "All people" table adds around the same row (plan
 * 2026-10-15-004 §6.4). Every one is optional and absent in a send card, whose
 * row is unchanged (pinned by `dispatch-log-render.test.tsx`).
 */
export interface SendLogRowExtras {
  /** Cells between the expand chevron and Person (the table's "When"). */
  leadingCells?: ReactNode;
  /** Cells between Person and Channel (the table's "Type"). */
  afterPersonCells?: ReactNode;
  /** Shown under the person's name (the table's "Only this person"). */
  personActions?: ReactNode;
  /** When set the name is a button that opens the person's panel on THIS row. */
  onOpenPerson?: () => void;
  /** Columns the expanded detail spans (the card's four by default). */
  detailColSpan?: number;
}

export function SendLogRow({
  playId,
  row,
  view,
  retry,
  recheck,
  leadingCells,
  afterPersonCells,
  personActions,
  onOpenPerson,
  detailColSpan = 4,
}: {
  playId: string;
  row: PlaybooksDispatchLogRow;
  view: RepStatusView;
  retry: SendRetry;
  recheck: SendRecheck;
} & SendLogRowExtras) {
  const { apiFetch } = useApi();
  const [open, setOpen] = useState(false);
  // A run Clay broke on a network error asks before Retry (sharing rules (o)):
  // Terra can't tell which Clay step broke. The sentence is the classifier's.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const displayName = row.contact_name ?? row.contact_email ?? 'Unnamed contact';
  // Both the classifier's verdict AND the raw eligibility rule, so the button
  // never appears on a row the server would answer `not_retryable` for — now
  // covering both arms: a failed START and a proven-safe Clay refusal (m).
  const canRetry = view.retryable && isRetryEligibleRow(row);
  const retryPending = retry.pendingLogIds.has(row.id);
  const retried = (row.retry_count ?? 0) > 0 && view.status !== 'failed_to_start';

  const sentCopy = useQuery({
    queryKey: sentCopyQueryKey(playId, row.id, row.retry_count ?? 0),
    queryFn: () => fetchSentCopyRequest(apiFetch, playId, row.id),
    enabled: open,
  });

  function onKeyDown(event: KeyboardEvent<HTMLTableRowElement>) {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setOpen((v) => !v);
  }

  return (
    <>
      <TableRow
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${displayName} — ${view.pillText}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        className={open ? 'cursor-pointer bg-bg-secondary' : 'cursor-pointer'}
      >
        <TableCell width={28}>
          <Icon type={open ? CaretDown : CaretRight} size="sm" className="text-content-placeholder" />
        </TableCell>
        {leadingCells}
        <TableCell>
          {onOpenPerson !== undefined ? (
            <button
              type="button"
              aria-label={displayName}
              className="cursor-pointer text-left text-sm font-medium hover:underline"
              onClick={(event) => {
                event.stopPropagation();
                onOpenPerson();
              }}
              // The row's own Enter/Space toggle must not fire from the name.
              onKeyDown={(event) => event.stopPropagation()}
            >
              {displayName}
            </button>
          ) : (
            <Text size="sm" weight="medium">
              {displayName}
            </Text>
          )}
          {row.bucket !== null && (
            <Text size="xs" variant="tertiary">
              {row.bucket}
            </Text>
          )}
          {personActions}
        </TableCell>
        {afterPersonCells}
        <TableCell>
          {view.channelLabel !== null ? (
            <Chip size="xs" color={row.channel === 'gong_engage' ? 'purple' : 'blue'}>
              {view.channelLabel}
            </Chip>
          ) : (
            <Text size="xs" variant="tertiary">
              —
            </Text>
          )}
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap items-center gap-2">
            <span data-rep-status={view.status}>
              <StatusBadge
                status={REP_STATUS_BADGE[view.status]}
                size="sm"
                // Terra override (flagged): Glaze's StatusBadge dot is static;
                // the prototype pulses it while a send is in progress.
                className={view.status === 'in_progress' ? '[&_.rounded-full]:motion-safe:animate-pulse' : undefined}
              >
                {view.pillText}
              </StatusBadge>
            </span>
            {canRetry && (
              <Button
                variant="danger-secondary"
                size="xs"
                aria-label={`Retry ${displayName}`}
                disabled={retryPending || retry.bulkPending || retry.isSettling(row)}
                onClick={(event) => {
                  event.stopPropagation();
                  if (view.retryConfirm !== null) setConfirmOpen(true);
                  else void retry.retryRow(row.id);
                }}
              >
                {retryPending && <LoadingSpinner size="sm" />}
                Retry
              </Button>
            )}
            {canRetry && view.retryConfirm !== null && (
              <ConfirmActionDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                title={`Retry ${displayName}?`}
                description={view.retryConfirm}
                confirmLabel="Retry anyway"
                variant="danger"
                pending={retryPending}
                onConfirm={() => {
                  void retry.retryRow(row.id).then(() => setConfirmOpen(false));
                }}
              />
            )}
            {view.needsRecheck && (
              <Button
                variant="ghost"
                size="xs"
                aria-label={`Check again for ${displayName}`}
                disabled={recheck.checking}
                onClick={(event) => {
                  event.stopPropagation();
                  recheck.checkAgain();
                }}
              >
                {recheck.checking && <LoadingSpinner size="sm" />}
                Check again
              </Button>
            )}
            {retried && (
              <Text size="xs" variant="placeholder">
                Retried
              </Text>
            )}
          </div>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="bg-bg-secondary">
          <TableCell colSpan={detailColSpan} className="pb-4 pl-10">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_300px]">
              <SentCopyPanel query={sentCopy} view={view} />
              <div className="flex flex-col gap-3">
                <WhoWherePanel row={row} view={view} sentCopy={sentCopy.data ?? null} />
                <WhatClayDidPanel
                  row={row}
                  view={view}
                  clayRunUrl={sentCopy.data?.clayRunUrl ?? null}
                  clayRunUrlKind={sentCopy.data?.clayRunUrlKind}
                />
                {(row.retry_count ?? 0) > 0 && <RetryHistoryPanel row={row} />}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
